/* eslint-disable import/no-cycle */

import { getAuth } from "firebase/auth";

import logger from "../log";

import { FETCH_TIMEOUT_MESSAGE, isAbortError } from "./fetchTimeout";

type Method = "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | "HEAD";

export class FetchError extends Error {
    statusCode: number;

    info: RequestInfo;

    method: Method;

    errorCode: string | null = null;

    /**
     * The parsed JSON error body, when the response had one. Errors carry more
     * than a message for some endpoints (a retry timestamp, a limit), and
     * re-deriving that from the message string is worse than keeping it.
     */
    body: unknown = null;

    constructor(
        code: number,
        method: Method,
        info: RequestInfo,
        message?: string,
        errorCode?: string,
        body?: unknown,
    ) {
        super(message);
        this.statusCode = code;
        this.method = method;
        this.info = info;
        this.errorCode = errorCode ?? null;
        this.body = body ?? null;
    }
}

export class PairRequiredError extends Error {
    method: Method;

    info: RequestInfo;

    constructor(method: Method, info: RequestInfo, message?: string) {
        super(message);
        this.method = method;
        this.info = info;
    }
}

export {
    FETCH_TIMEOUT_MESSAGE,
    isAbortError,
    timeoutInit,
} from "./fetchTimeout";

/**
 * A rejection rendered for a user rather than for a log.
 *
 * The rule is by status: a 4xx body is written for whoever made the request
 * ("Invalid APTA ID"), so it is shown as-is, while a 5xx body is written for
 * us - `aptaFilter` answers `String(e)` - and is replaced by the caller's
 * `fallback`. Anything that is not a `FetchError` at all (a `TypeError` from a
 * dropped connection, say) carries engine wording and is replaced too.
 */
export function userFacingFetchMessage(e: unknown, fallback: string): string {
    if (isAbortError(e)) return FETCH_TIMEOUT_MESSAGE;
    if (e instanceof FetchError && e.statusCode < 500 && e.message) {
        return e.message;
    }
    return fallback;
}

export default async function fetchApi<T>(
    info: RequestInfo,
    method: Method = "GET",
    body?: unknown,
    init: RequestInit = {},
): Promise<T> {
    const baseHeaders: HeadersInit = {
        accept: "application/json, text/plain, */*",
        "content-type": "application/json",
        "x-requested-with": "fetch",
    };

    const { currentUser } = getAuth();
    if (currentUser) {
        const token = await currentUser.getIdToken();
        baseHeaders.authorization = `Bearer ${token}`;
    }

    init.method = method;
    init.headers = { ...baseHeaders, ...init.headers };
    if (body) init.body = JSON.stringify(body);
    // Only add a default timeout if no signal is provided at all
    if (!init.signal && AbortSignal?.timeout) {
        init.signal = AbortSignal.timeout(10_000);
    }

    logger.debug(`FETCHING - [${init.method || "GET"}] ${info as string}`);

    const res = await fetch(info, init);

    logger.debug(
        `[${init.method || "GET"}] ${info as string}: ${res.status} - [${res.statusText}] - ${res.ok ? "ok" : "failed"}`,
    );

    if (res.headers.get("content-type")?.includes("application/json")) {
        const resJson = (await res.json()) as unknown;
        if (!res.ok) {
            const err = resJson as { message: string; code?: string };
            throw new FetchError(
                res.status,
                method,
                info,
                err.message,
                err.code,
                resJson,
            );
        }
        return resJson as T;
    }

    if (!res.ok) {
        throw new FetchError(
            res.status,
            method,
            info,
            `HTTP fetch error: ${res.status}: ${await res.text()}`,
        );
    }

    return null as T;
}

/**
 * Fetch binary content (an image, say) that needs the user's credentials, which
 * an <img src> can't send: requests authenticate with a bearer token, not a
 * cookie.
 */
export async function fetchApiBlob(
    info: RequestInfo,
    init: RequestInit = {},
): Promise<Blob> {
    const headers: Record<string, string> = { "x-requested-with": "fetch" };
    const { currentUser } = getAuth();
    if (currentUser) {
        headers.authorization = `Bearer ${await currentUser.getIdToken()}`;
    }
    if (!init.signal && AbortSignal?.timeout) {
        init.signal = AbortSignal.timeout(20_000);
    }
    const res = await fetch(info, {
        ...init,
        headers: { ...headers, ...init.headers },
    });
    if (!res.ok) {
        throw new FetchError(
            res.status,
            (init.method as Method | undefined) ?? "GET",
            info,
            `HTTP fetch error: ${res.status}: ${(await res.text()).slice(0, 200)}`,
        );
    }
    return res.blob();
}

export async function pairedFetchApi<T>(
    trainerId: number | undefined,
    apiUrl: string,
    method: Method = "GET",
    body?: unknown,
    init: RequestInit = {},
): Promise<T> {
    if (!trainerId) {
        throw new PairRequiredError(method, apiUrl, "No trainer id provided");
    }

    const baseUrl = `/trainer/${trainerId}`;
    const fullUrl = `${baseUrl}${apiUrl.startsWith("/") ? "" : "/"}${apiUrl}`;

    return fetchApi<T>(fullUrl, method, body, init);
}

export function logFetchError(e: unknown, message?: string) {
    let context = "";
    if (message) {
        context = `${message}:\r\n`;
    }
    if (e instanceof FetchError) {
        logger.error(
            `${context}${e.message}, status: ${e.statusCode}, [${e.method}] - ${JSON.stringify(e.info)}`,
        );
    } else if (e instanceof PairRequiredError) {
        logger.error(
            `${context}${e.message}, [${e.method}] - ${JSON.stringify(e.info)}`,
        );
    } else {
        logger.error(
            `${context}Unexpected error fetching: ${JSON.stringify(e, ["message", "type", "name"])}`,
        );
    }
}
